import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Alert,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { Link } from "react-router-dom";
import Header from "../pages/Header";
import PortalNavigation from "./PortalNavigation";
import { PODBORKI_IMAGE_BASE_URL } from "../api/podborki";
import podborkiStore from "../stores/podborkiStore";

const Podborki: React.FC = observer(() => {
  const [search, setSearch] = useState("");

  useEffect(() => {
    podborkiStore.fetchPodborki();
  }, []);

  const visibleCollections = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();

    return [...podborkiStore.podborki]
      .filter(({ name }) => name.toLocaleLowerCase().includes(query))
      .sort((first, second) =>
        second.sort - first.sort || first.name.localeCompare(second.name, "ru")
      );
  }, [search, podborkiStore.podborki]);

  return (
    <Box minHeight="100vh" display="flex" flexDirection="column">
      <Header />

      <Box
        display="flex"
        flexDirection="column"
        flexGrow={1}
        p={2}
      >
        <PortalNavigation />

        <Box sx={{ width: "100%", maxWidth: 1600, mx: "auto" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              mb: 2,
            }}
          >
            <TextField
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Поиск по названию"
              size="small"
              sx={{ width: { xs: "100%", sm: 320 } }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Очистить поиск"
                        onClick={() => setSearch("")}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                },
              }}
            />

            <Button
              component={Link}
              to="/collections/create"
              variant="contained"
              startIcon={<AddIcon />}
            >
              Создать
            </Button>
          </Box>

          {podborkiStore.error && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => podborkiStore.fetchPodborki()}>
                  Повторить
                </Button>
              }
              sx={{ mb: 2 }}
            >
              {podborkiStore.error}
            </Alert>
          )}

          <TableContainer component={Paper} variant="outlined">
            <Table aria-label="Таблица подборок">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.100" }}>
                  <TableCell sx={{ width: 160, fontWeight: 700 }}>
                    Изображение
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Название</TableCell>
                  <TableCell align="center" sx={{ width: 150, fontWeight: 700 }}>
                    Сортировка
                  </TableCell>
                  <TableCell align="center" sx={{ width: 120, fontWeight: 700 }}>
                    ID
                  </TableCell>
                  <TableCell align="right" sx={{ width: 130, fontWeight: 700 }}>
                    Действия
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {visibleCollections.map((collection) => (
                  <TableRow key={collection.id} hover>
                    <TableCell>
                      <Box component={Link} to={`/collections/${collection.id}`} display="inline-block">
                        <Box
                          component="img"
                          src={`${PODBORKI_IMAGE_BASE_URL}/${collection.id}.jpg`}
                          alt={collection.name}
                          sx={{
                            display: "block",
                            width: 128,
                            height: 64,
                            borderRadius: 1,
                            objectFit: "cover",
                            bgcolor: "grey.200",
                            transition: "opacity 0.2s",
                            "&:hover": { opacity: 0.8 },
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        component={Link}
                        to={`/collections/${collection.id}`}
                        sx={{
                          color: "primary.main",
                          fontWeight: 600,
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {collection.name}
                      </Box>
                    </TableCell>
                    <TableCell align="center">{collection.sort}</TableCell>
                    <TableCell align="center">{collection.id}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Редактировать">
                        <IconButton
                          component={Link}
                          to={`/collections/${collection.id}`}
                          color="primary"
                          aria-label={`Редактировать ${collection.name}`}
                        >
                          <EditOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Удалить">
                        <IconButton color="error" aria-label={`Удалить ${collection.name}`}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}

                {podborkiStore.loading && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                )}

                {!podborkiStore.loading && visibleCollections.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography color="text.secondary" align="center" sx={{ py: 5 }}>
                        {search ? "Подборки не найдены" : "Список подборок пока пуст"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
});

export default Podborki;
